import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row, Tooltip, Button, Grid } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import { RootState } from '../../../../redux/store';
import AppTable from '../../../../components/AppTable/AppTable';
import { colors } from '../../../../utils/colors/colors';
import { User } from '../../../../redux/reducers/clients/clients.types';
import UpdateUserDrawer from '../UpdateUserDrawer/UpdateUserDrawer';
import { Key } from 'antd/es/table/interface';

const { useBreakpoint } = Grid;

const UsersTable = () => {
    const { t } = useTranslation('clients');
    const screens = useBreakpoint();

    const { users: { data, loading } } = useSelector((state: RootState) => state.clients);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>();

    const handleEditUser = (record: User) => {
        setSelectedUserId(record.id);
        setIsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedUserId(undefined);
    };

    const renderStatus = (isActive: boolean, record: User) => {
        const isEffectivelyActive = record.companyIsActive && isActive;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: isEffectivelyActive ? colors.blueitGreen : colors.blueitRed
                }} />
                <span className="base-table-text">{isEffectivelyActive ? t("active") : t("passive")}</span>
            </div>
        );
    };


    const columns = [
        {
            title: <span className="base-table-text">{t("full_name")}</span>,
            key: 'fullName',
            render: (record: User) => {
                const fullName = `${record.name} ${record.surname}`.trim();
                return <span className="base-table-text">{fullName}</span>;
            },
            filters: Array.from(new Set(data?.map(item => `${item.name} ${item.surname}`.trim()) || []))
                .map(fullName => ({ text: fullName, value: fullName })),
            filterSearch: true,
            onFilter: (value: boolean | Key, record: User) => `${record.name} ${record.surname}`.trim() === value,
        },
        {
            title: <span className="base-table-text">{t("email")}</span>,
            dataIndex: 'email',
            key: 'email',
            render: (text: string) => <span className="base-table-text">{text}</span>
        },
        {
            title: <span className="base-table-text">{t("status")}</span>,
            dataIndex: 'isActive',
            key: 'isActive',
            render: renderStatus,
            filters: [
                { text: t("active"), value: true },
                { text: t("passive"), value: false }
            ],
            onFilter: (value: boolean | Key, record: User) => record.isActive === value,
        },
        {
            title: <span className="base-table-text">{t("affiliated_company")}</span>,
            dataIndex: 'companyName',
            key: 'companyName',
            render: (text: string) => <span className="base-table-text">{text}</span>
        },
        {
            title: <span className="base-table-text">{t("user_role")}</span>,
            dataIndex: 'userRoleName',
            key: 'userRoleName',
            render: (text: string) => <span className="base-table-text">{t(text)}</span>
        },
        {
            title: <span className="base-table-text">{t("userActions")}</span>,
            dataIndex: 'actions',
            key: 'actions',
            align: 'center' as const,
            render: (_: any, record: User) => {
                return (
                    <Row justify={"center"}>
                        <Col>
                            <Tooltip color='white' title={t("edit_user")} overlayInnerStyle={{ color: colors.blueitNavyBlue }}>
                                <Button
                                    type="text"
                                    icon={<FormOutlined style={{ color: "#00000073", fontSize: screens.xxl ? 20 : screens.xl ? 16 : 14 }} />}
                                    onClick={() => handleEditUser(record)}
                                />
                            </Tooltip>
                        </Col>
                    </Row>
                )
            }
        }
    ];

    return (
        <>
            <Row>
                <Col style={{ overflow: "visible", overflowX: "auto" }} xs={24}>
                    <AppTable
                        className='base-table-body'
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                        paginationConfig={{
                            pageSize: 10,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} ${t("record")}`
                        }}
                    />
                </Col>
            </Row>
            <UpdateUserDrawer
                open={isDrawerOpen}
                onClose={handleDrawerClose}
                userId={selectedUserId}
            />
        </>
    )
}

export default UsersTable;